// Centralized dealer-address formatting so every surface (Route Tracker, Work Summary
// drawer, Daily Route Summary page, printable HTML) renders the SAME address text.
// Mirrors the convention already used by printRoutes.ts.

// Allow undefined explicitly so callers under exactOptionalPropertyTypes can pass
// values like `street: maybeUndefined` without ceremony.
export interface AddressParts {
  street: string | null | undefined;
  /** Apt / unit / suite (customer stops), right after the street */
  addressLine2?: string | null | undefined;
  city: string | null | undefined;
  region: string | null | undefined; // US state
  postalCode: string | null | undefined; // ZIP
}

// "123 Main St, Apt 4, Springfield, IL 62701" — gracefully omits any missing part.
// Returns an empty string when nothing is present; callers can choose their own
// fallback (typically "—" in a table cell, "" when concatenating).
export function formatStreetAddress(parts: AddressParts): string {
  const stateZip = [parts.region ?? '', parts.postalCode ?? '']
    .map(s => s.trim())
    .filter(Boolean)
    .join(' ');
  return [parts.street ?? '', parts.addressLine2 ?? '', parts.city ?? '', stateZip]
    .map(s => s.trim())
    .filter(Boolean)
    .join(', ');
}

// Pull address parts out of a RouteRecallDetail — kept here so the call sites
// don't have to remember the field names each time.
export function addressPartsFromRecall(recall: {
  dealerAddress?: string;
  addressLine2?: string | null;
  dealerCity?: string;
  dealerRegion?: string;
  dealerPostalCode?: string;
}): AddressParts {
  return {
    street: recall.dealerAddress,
    addressLine2: recall.addressLine2,
    city: recall.dealerCity,
    region: recall.dealerRegion,
    postalCode: recall.dealerPostalCode
  };
}
